/**
 * Campo de teléfono con código de país.
 *
 * Por qué existe: un input de texto suelto acepta "no tiene" o "el del vecino",
 * y después nadie puede llamar a ese paciente. Acá solo entran dígitos, y el
 * país va aparte para que el número quede guardado en formato internacional
 * (+51987654321) — que es lo que necesita cualquier envío de SMS o WhatsApp el
 * día que se conecte, sin tener que adivinar de dónde era cada número.
 *
 * Perú por defecto porque es un policlínico peruano y casi todos los números lo
 * son; el resto de países está a un clic para el paciente extranjero, que en la
 * frontera y en Lima no es raro.
 */
import { useMemo } from "react";

interface Pais {
  codigo: string;
  bandera: string;
  nombre: string;
  /** Dígitos que tiene el número sin el código de país. Solo para guiar. */
  digitos: number;
}

// Perú primero, y después los vecinos de donde viene la mayoría de extranjeros
// atendidos. No es un catálogo mundial a propósito: una lista de 200 países
// hace más lento elegir el que se usa el 99% de las veces.
export const PAISES: Pais[] = [
  { codigo: "+51", bandera: "🇵🇪", nombre: "Perú", digitos: 9 },
  { codigo: "+591", bandera: "🇧🇴", nombre: "Bolivia", digitos: 8 },
  { codigo: "+56", bandera: "🇨🇱", nombre: "Chile", digitos: 9 },
  { codigo: "+57", bandera: "🇨🇴", nombre: "Colombia", digitos: 10 },
  { codigo: "+593", bandera: "🇪🇨", nombre: "Ecuador", digitos: 9 },
  { codigo: "+58", bandera: "🇻🇪", nombre: "Venezuela", digitos: 10 },
  { codigo: "+54", bandera: "🇦🇷", nombre: "Argentina", digitos: 10 },
  { codigo: "+55", bandera: "🇧🇷", nombre: "Brasil", digitos: 11 },
  { codigo: "+34", bandera: "🇪🇸", nombre: "España", digitos: 9 },
  { codigo: "+1", bandera: "🇺🇸", nombre: "EE.UU.", digitos: 10 },
];

const PERU = PAISES[0];

/** Parte "+51987654321" en país y número. Un número sin código se asume peruano. */
export function partirTelefono(valor: string | null | undefined): {
  pais: Pais;
  numero: string;
} {
  const limpio = (valor ?? "").replace(/[^\d+]/g, "");
  if (limpio.startsWith("+")) {
    // El más largo primero: +591 antes que +5, para no confundir Bolivia.
    const pais = [...PAISES]
      .sort((a, b) => b.codigo.length - a.codigo.length)
      .find((p) => limpio.startsWith(p.codigo));
    if (pais) return { pais, numero: limpio.slice(pais.codigo.length) };
  }
  return { pais: PERU, numero: limpio.replace(/\D/g, "") };
}

export function TelefonoInput({
  id,
  value,
  onChange,
  disabled = false,
  autoFocus = false,
}: {
  id?: string;
  value: string | null | undefined;
  /** Devuelve el número completo ("+51987654321") o "" si se vació. */
  onChange: (valor: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
}) {
  const { pais, numero } = useMemo(() => partirTelefono(value), [value]);

  function emitir(codigo: string, digitos: string) {
    onChange(digitos ? `${codigo}${digitos}` : "");
  }

  return (
    <div className="flex gap-2">
      <select
        aria-label="Código de país"
        value={pais.codigo}
        disabled={disabled}
        onChange={(e) => emitir(e.target.value, numero)}
        className="w-[5.5rem] shrink-0 rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--color-link)] disabled:bg-slate-50"
      >
        {PAISES.map((p) => (
          <option key={p.codigo} value={p.codigo}>
            {p.bandera} {p.codigo}
          </option>
        ))}
      </select>
      <input
        id={id}
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        autoFocus={autoFocus}
        value={numero}
        disabled={disabled}
        // Se filtra al escribir y no al guardar: el que pega un número con
        // espacios o guiones no tiene por qué limpiarlo a mano.
        onChange={(e) => emitir(pais.codigo, e.target.value.replace(/\D/g, "").slice(0, 15))}
        placeholder={"9".repeat(Math.min(pais.digitos, 9))}
        // min-w: un número de nueve dígitos tiene que caber entero sin
        // recortarse, esté donde esté el campo. Se dicta por teléfono y se
        // relee en voz alta para confirmarlo.
        className="w-full min-w-[8.5rem] rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-sm tracking-wide focus:outline-none focus:ring-2 focus:ring-[color:var(--color-link)] disabled:bg-slate-50"
      />
    </div>
  );
}

/** Cómo se muestra un teléfono guardado: "+51 987 654 321". */
export function formatearTelefono(valor: string | null | undefined): string {
  if (!valor) return "—";
  const { pais, numero } = partirTelefono(valor);
  if (!numero) return "—";
  const grupos = numero.replace(/(\d{3})(?=\d)/g, "$1 ");
  return `${pais.codigo} ${grupos}`.trim();
}
